import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { MessageCircle } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WHATSAPP_NUMBER = '5575981872440';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, vim do site de nódulo e gostaria de agendar uma consulta com dr. Mateus')}`;

const faqs = [
  {
    q: 'Todo nódulo é câncer?',
    a: 'Não. A maioria dos nódulos descobertos são benignos. Nódulos pequenos, com bordas lisas e sem crescimento em tomografias seriadas geralmente são completamente seguros. Mas precisam de acompanhamento profissional para confirmar.',
  },
  {
    q: 'Se eu descobrir um nódulo, vou morrer de câncer?',
    a: 'Não é assim que funciona! Descobrir um nódulo é uma oportunidade de vigilância e diagnóstico precoce — se for maligno, a detecção prévia aumenta as chances de cura significativamente. A maioria dos nódulos são benignos ou, se malignos, são diagnosticados em estágios tratáveis.',
  },
  {
    q: 'Preciso fazer biópsia obrigatoriamente?',
    a: 'Não. Biópsia é indicada quando características do nódulo sugerem malignidade ou quando há dúvida diagnóstica. Por isso a importância da avaliação pelo Cirurgião Torácico. Muitos nódulos precisam apenas de acompanhamento radiológico.',
  },
  {
    q: 'Qual é a diferença entre biópsia percutânea e ressecção cirúrgica?',
    a: 'Biópsia percutânea: coleta amostra com agulha fina (diagnóstico apenas). Ressecção cirúrgica: remove o nódulo e parte do pulmão (diagnóstico + tratamento simultâneo). A escolha depende de fatores como localização do nódulo, diagnóstico previsto e preferência do paciente.',
  },
  {
    q: 'Biópsia percutânea é segura?',
    a: 'Sim. Taxa de complicações graves é menor que 1%. Complicação mais comum é pneumotórax (ar no espaço pleural) em aproximadamente 2-5% dos casos — e a maioria resolve sem intervenção. O procedimento é realizado com toda a segurança necessária.',
  },
  {
    q: 'Quanto tempo demora o resultado da biópsia?',
    a: 'Normalmente 5 a 7 dias úteis. Após o resultado agendamos uma nova consulta com Dr. Mateus para discutir o diagnóstico e definir os próximos passos. Nenhuma dúvida fica sem resposta.',
  },
  {
    q: 'Se o nódulo for maligno, qual é o prognóstico?',
    a: 'Prognóstico depende de vários fatores: tamanho, tipo de câncer, estágio, idade do paciente. Mas diagnóstico precoce (quando o nódulo ainda é pequeno) melhora significativamente o prognóstico. É exatamente por isso que a avaliação precoce é tão importante.',
  },
  {
    q: 'Acompanhamento radiológico é seguro? Vou ficar sendo “vigiado” para sempre?',
    a: 'Acompanhamento é seguro e baseado em guidelines internacionais (Fleischner Society). Nódulos benignos confirmados não exigem vigilância perpétua — podem ser encerrados após 2 anos de estabilidade. Você não fica preso a isso indefinidamente.',
  },
  {
    q: 'Qual é o custo da avaliação e biópsia?',
    a: 'Caso não seja coberto pelo seu Convênio, entre em contato com nossa equipe para realizar o orçamento. Trabalhamos para garantir acesso ao melhor cuidado possível.',
  },
  {
    q: 'Posso continuar trabalhando durante o acompanhamento?',
    a: 'Sim. Se apenas acompanhamento radiológico, não há nenhuma restrição para as atividades habituais. Após biópsia percutânea, o retorno ao trabalho ocorre em 24-48h. Após ressecção cirúrgica, o retorno depende do tipo de trabalho.',
  },
];

export default function NoduloFAQ() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      id="faq"
      ref={ref}
      className={`py-24 md:py-32 bg-white transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Tire Suas Dúvidas
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-4 leading-tight">
            Dúvidas Sobre Nódulo Pulmonar?<br />Respondemos Aqui.
          </h2>
          <p className="text-[#212121]/65 text-base">
            Perguntas reais de pacientes reais. Respondidas com honestidade.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-gray-100 rounded-xl bg-[#F5F5F5] px-6 overflow-hidden"
              >
                <AccordionTrigger className="font-poppins font-semibold text-[#212121] text-sm md:text-base text-left hover:no-underline hover:text-[#0D47A1] py-5 [&[data-state=open]]:text-[#0D47A1] transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#212121]/70 text-sm md:text-base leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="text-center mt-10">
          <p className="text-[#212121]/60 text-sm mb-4">Sua dúvida não está aqui?</p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-8 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            Envie mensagem no WhatsApp
          </a>
        </div>

      </div>
    </section>
  );
}
